import React, { useEffect, useState } from 'react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';

const Row = ({ item, index, count, onMove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-2 rounded-lg border px-2 py-2 bg-white dark:bg-slate-900 ${isDragging ? 'shadow-lg border-blue-400 z-10 relative' : 'border-slate-200 dark:border-slate-700'}`}
      data-testid={`arrange-row-${item.id}`}
    >
      <button type="button" className="touch-none cursor-grab p-1.5 text-slate-400 hover:text-slate-700" aria-label="Drag to reorder" {...attributes} {...listeners} data-testid={`arrange-handle-${item.id}`}>
        <GripVertical className="h-5 w-5" />
      </button>
      <span className="w-6 text-xs text-slate-400 font-mono">{index + 1}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">Unit {item.unit_number}</p>
        {item.driver_name && <p className="text-xs text-slate-500 truncate">{item.driver_name}</p>}
      </div>
      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={index === 0} onClick={() => onMove(index, index - 1)} aria-label="Move up" data-testid={`arrange-up-${item.id}`}><ArrowUp className="h-4 w-4" /></Button>
      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={index === count - 1} onClick={() => onMove(index, index + 1)} aria-label="Move down" data-testid={`arrange-down-${item.id}`}><ArrowDown className="h-4 w-4" /></Button>
    </li>
  );
};

/** Drag-and-drop editor for the order units appear in on the Summary page (saved per user). */
export const ArrangeUnitsDialog = ({ open, onOpenChange, units, onSave, saving }) => {
  const [items, setItems] = useState(units);
  useEffect(() => { if (open) setItems(units); }, [open, units]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const move = (from, to) => setItems((prev) => arrayMove(prev, from, to));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    move(items.findIndex((i) => i.id === active.id), items.findIndex((i) => i.id === over.id));
  };
  const sortByNumber = () => setItems((prev) => [...prev].sort((a, b) => String(a.unit_number).localeCompare(String(b.unit_number), undefined, { numeric: true })));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col" data-testid="arrange-units-dialog">
        <DialogHeader>
          <DialogTitle>Arrange units</DialogTitle>
          <DialogDescription>Drag units (or use the arrows) to set the order they appear on the Summary page. Only you see this order.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <ul className="space-y-2">
                {items.map((item, i) => <Row key={item.id} item={item} index={i} count={items.length} onMove={move} />)}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="outline" onClick={sortByNumber} data-testid="arrange-reset-btn"><RotateCcw className="h-4 w-4 mr-1.5" />Sort by unit #</Button>
          <Button type="button" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving} onClick={() => onSave(items.map((i) => i.id))} data-testid="arrange-save-btn">
            {saving ? 'Saving…' : 'Save order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
